/**
 * Audit Trail Zod Schemas
 *
 * Type-safe schemas for the audit_log table (F08).
 * Used by auditLogger (fire-and-forget inserts) and AuditLogRepository (queries).
 *
 * @module services/schemas/auditSchemas
 */

import { z } from 'zod';

/**
 * Valid audit actions. Maps to the CHECK constraint on audit_log.action.
 */
export const AuditActionSchema = z.enum([
  'view',
  'create',
  'update',
  'delete',
  'export',
  'login',
  'logout',
  'search',
  'access_pii',
]);

export type AuditAction = z.infer<typeof AuditActionSchema>;

/**
 * Entity types that trigger automatic PII flagging.
 * When entityType is one of these, the action is auto-set to 'access_pii'.
 */
export const PII_ENTITY_TYPES = ['employees', 'suspects', 'users'] as const;

/**
 * Input for logging an audit entry (fire-and-forget).
 */
export const AuditLogInputSchema = z.object({
  tenantId: z.string().uuid(),
  userId: z.string().uuid(),
  action: AuditActionSchema,
  entityType: z.string().min(1),
  entityId: z.string().uuid().optional(),
  details: z.record(z.string(), z.unknown()).optional(),
});

export type AuditLogInput = z.infer<typeof AuditLogInputSchema>;

/**
 * Database row from audit_log table (SELECT result).
 */
export const AuditLogEntrySchema = z.object({
  id: z.string().uuid(),
  tenant_id: z.string().uuid(),
  user_id: z.string().uuid(),
  action: AuditActionSchema,
  resource_type: z.string(),
  resource_id: z.string().uuid().nullable(),
  metadata: z.record(z.string(), z.unknown()).nullable(),
  request_meta: z.record(z.string(), z.unknown()).nullable(),
  created_at: z.string(),
});

export type AuditLogEntry = z.infer<typeof AuditLogEntrySchema>;

/**
 * Filters for querying audit logs.
 */
export const AuditLogFiltersSchema = z.object({
  userId: z.string().uuid().optional(),
  action: AuditActionSchema.optional(),
  actions: z.array(AuditActionSchema).optional(),
  entityType: z.string().optional(),
  entityId: z.string().uuid().optional(),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  page: z.number().int().min(1).default(1),
  limit: z.number().int().min(1).max(100).default(50),
});

export type AuditLogFilters = z.infer<typeof AuditLogFiltersSchema>;

/**
 * Paginated audit log response.
 */
export interface AuditLogPage {
  data: AuditLogEntry[];
  count: number;
  page: number;
  limit: number;
  totalPages: number;
}

/**
 * PII access summary row (grouped by user).
 */
export interface PIIAccessSummary {
  userId: string;
  userName: string;
  accessCount: number;
  lastAccess: string;
  entityTypes: string[];
}
